import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PasswordTokenInfo } from '@nexops/shared';
import { api, getErrorCode, getErrorStatus } from '@/shared/lib/api';
import { authApi } from '@/features/auth/auth.api';
import { useAuthStore } from '@/features/auth/auth.store';

export interface TwoFactorSetup {
  secret: string;
  otpauthUrl: string;
  qrDataUrl: string;
}

export const accountKeys = { twoFactor: ['account', '2fa'] as const };

/** Re-reads `/auth/me`: the session limits (temporary password, required 2FA) lift as soon as the user resolves them. */
export async function refreshSession(): Promise<void> {
  useAuthStore.getState().setUser((await authApi.me()).user);
}

export function useTwoFactorStatus() {
  return useQuery({ queryKey: accountKeys.twoFactor, queryFn: () => api.get<{ enabled: boolean }>('/auth/2fa/status'), staleTime: 30_000, retry: false, meta: { silent: true } });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) => api.post<{ success: boolean; endedSessions: number }>('/auth/password', body),
    meta: { silent: true },
    onSuccess: refreshSession,
  });
}

export function useTwoFactorSetup() {
  return useMutation({ mutationFn: () => api.post<TwoFactorSetup>('/auth/2fa/setup'), meta: { silent: true } });
}

export function useTwoFactorEnable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.post<{ enabled: boolean }>('/auth/2fa/enable', { code }),
    meta: { silent: true },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: accountKeys.twoFactor });
      await refreshSession();
    },
  });
}

export function useTwoFactorDisable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.post<{ enabled: boolean }>('/auth/2fa/disable', { code }),
    meta: { silent: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: accountKeys.twoFactor }),
  });
}

export const passwordLinkApi = {
  forgot: (email: string) => api.post<{ ok: true }>('/auth/password/forgot', { email }),
  check: (token: string) => api.post<PasswordTokenInfo>('/auth/password/check', { token }),
  reset: (token: string, password: string) => api.post<{ ok: true }>('/auth/password/reset', { token, password }),
};

export type SecurityError = 'currentPassword' | 'unchanged' | 'invalidCode' | 'required' | 'tooMany' | 'network' | 'invalidLink' | 'generic';

/** Maps an API failure to a message of the account screens. */
export function securityError(error: unknown): SecurityError {
  const code = getErrorCode(error);
  const status = getErrorStatus(error);
  if (code === 'INVALID_CURRENT_PASSWORD') return 'currentPassword';
  if (code === 'PASSWORD_UNCHANGED') return 'unchanged';
  if (code === 'INVALID_TOTP') return 'invalidCode';
  if (code === 'TWO_FACTOR_REQUIRED') return 'required';
  if (code === 'INVALID_TOKEN') return 'invalidLink';
  if (status === 429) return 'tooMany';
  if (status === undefined) return 'network';
  return 'generic';
}

/** Rough strength of a new password (0–4): length plus variety. The server only enforces the minimum length. */
export function passwordStrength(p: string): number {
  if (p.length < 10) return p.length === 0 ? 0 : 1;
  const variety = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((re) => re.test(p)).length;
  return Math.min(4, 1 + Math.max(0, variety - 1) + (p.length >= 14 ? 1 : 0));
}
